import React from 'react';

const LegacyFeaturesSection = () => (
  <section
    id="features"
    className="px-6 py-20 md:py-28 bg-gradient-to-b from-[#0f1115] via-[#131820] to-[#0f1115]"
  >
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-bold text-[#f8f5ed] mb-4">
          Everything your chama needs.
          <br />
          <span className="text-emerald-400">Nothing it doesn&apos;t.</span>
        </h2>
        <p className="text-lg text-[#d8d2c5] max-w-2xl mx-auto">
          No complicated menus. No features you&apos;ll never use. Just the tools that matter.
        </p>
      </div>

      <div className="space-y-20">
        {/* Feature 1 - Alternating layout */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-block px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-300 text-sm font-medium mb-4">
              💰 Money In
            </div>
            <h3 className="text-3xl font-bold text-[#f8f5ed] mb-4">
              Contributions that actually come in on time
            </h3>
            <p className="text-lg text-[#d8d2c5] mb-6 leading-relaxed">
              Send M-Pesa payment requests right from the app. Members pay with one tap. You see
              payments instantly—no more &quot;I sent, check your phone&quot; confusion.
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-[#d8d2c5]">
                <svg
                  className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Flexible schedules—weekly, monthly, or custom</span>
              </li>
              <li className="flex items-start gap-3 text-[#d8d2c5]">
                <svg
                  className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Partial payments tracked automatically</span>
              </li>
              <li className="flex items-start gap-3 text-[#d8d2c5]">
                <svg
                  className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Automatic reminders before due dates</span>
              </li>
            </ul>
          </div>
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-transparent rounded-3xl blur-3xl" />
            <div className="relative bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-3xl p-8 backdrop-blur">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                  <span className="text-[#f8f5ed] font-medium">January Contributions</span>
                  <span className="text-emerald-400 font-bold">98% paid</span>
                </div>
                <div className="p-4 bg-white/5 border border-white/5 rounded-xl">
                  <div className="flex justify-between text-sm text-[#d8d2c5] mb-2">
                    <span>M-Pesa payments</span>
                    <span className="text-cyan-400">Auto-synced</span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400 w-[98%]" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature 2 - Reversed layout */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="lg:order-2">
            <div className="inline-block px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-300 text-sm font-medium mb-4">
              🤝 Loans & Welfare
            </div>
            <h3 className="text-3xl font-bold text-[#f8f5ed] mb-4">
              Support members when they need it most
            </h3>
            <p className="text-lg text-[#d8d2c5] mb-6 leading-relaxed">
              Someone needs school fees? Medical help? A business loan? Handle everything from
              application to approval to repayment—all transparent, all tracked.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-white/5 border border-white/5 rounded-xl">
                <div className="text-2xl font-bold text-cyan-300 mb-1">2-min</div>
                <div className="text-sm text-[#d8d2c5]">Loan approval</div>
              </div>
              <div className="p-4 bg-white/5 border border-white/5 rounded-xl">
                <div className="text-2xl font-bold text-emerald-300 mb-1">Auto</div>
                <div className="text-sm text-[#d8d2c5]">Interest calc</div>
              </div>
            </div>
          </div>
          <div className="lg:order-1 relative">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-transparent rounded-3xl blur-3xl" />
            <div className="relative bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-3xl p-8 backdrop-blur">
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
                  <div>
                    <div className="text-[#f8f5ed] font-medium">Medical Emergency</div>
                    <div className="text-cyan-400 text-xs">KES 50,000</div>
                  </div>
                  <span className="px-3 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-xs font-medium">
                    Approved
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 border border-white/5 rounded-lg">
                  <div>
                    <div className="text-[#f8f5ed] font-medium">Business Loan</div>
                    <div className="text-[#d8d2c5] text-xs">KES 200,000</div>
                  </div>
                  <span className="px-3 py-1 bg-amber-500/20 text-amber-300 rounded-full text-xs font-medium">
                    Pending
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 border border-white/5 rounded-lg">
                  <div>
                    <div className="text-[#f8f5ed] font-medium">School Fees</div>
                    <div className="text-[#d8d2c5] text-xs">KES 30,000</div>
                  </div>
                  <span className="px-3 py-1 bg-cyan-500/20 text-cyan-300 rounded-full text-xs font-medium">
                    Active
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature 3 - Full width different style */}
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-block px-3 py-1 rounded-full bg-amber-500/10 text-amber-300 text-sm font-medium mb-4">
            📊 Reports & Transparency
          </div>
          <h3 className="text-3xl font-bold text-[#f8f5ed] mb-4">
            Everyone sees exactly where the money is
          </h3>
          <p className="text-lg text-[#d8d2c5] mb-10 leading-relaxed">
            No more &quot;trust me, I kept the records.&quot; Every transaction, every loan, every
            contribution—automatically tracked and visible to your members.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 bg-gradient-to-br from-white/10 to-white/5 border border-white/10 rounded-2xl">
              <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-4 mx-auto">
                <svg
                  className="w-6 h-6 text-emerald-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-[#f8f5ed] mb-2">Real-time dashboards</h4>
              <p className="text-sm text-[#d8d2c5]">
                See who&apos;s paid, who hasn&apos;t, and what&apos;s coming up next
              </p>
            </div>
            <div className="p-6 bg-gradient-to-br from-white/10 to-white/5 border border-white/10 rounded-2xl">
              <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center mb-4 mx-auto">
                <svg
                  className="w-6 h-6 text-cyan-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-[#f8f5ed] mb-2">Export everything</h4>
              <p className="text-sm text-[#d8d2c5]">
                PDF and Excel reports ready for your AGM or auditor
              </p>
            </div>
            <div className="p-6 bg-gradient-to-br from-white/10 to-white/5 border border-white/10 rounded-2xl">
              <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center mb-4 mx-auto">
                <svg
                  className="w-6 h-6 text-amber-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-[#f8f5ed] mb-2">Complete audit trail</h4>
              <p className="text-sm text-[#d8d2c5]">Every action logged. Nothing disappears.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default LegacyFeaturesSection;
